import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import { Button, Flex, Select, Space, Typography } from 'antd';
import { type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import AuthorizedComponent from '../../../components/authorized-component';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import { AuthContext } from '../../../contexts/auth-context-provider';
import { Customer } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';
import ScheduledMealsCalendarModal from '../../scheduling/cycle-menus/scheduled-meals/scheduled-meals-calendar.modal';
import CycleMenuDownloadModal from '../customer-orders/cycle-menu-download.modal';

const MenusPage = (): ReactNode => {
  const { session } = useContext(AuthContext);
  const notification = useNotification();

  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const customers = useQuery<Customer[]>([
    'customer',
    {
      include: ['sites.routes', 'customerSetting'],
      filter: session?.claims.role === Roles.ROLE_HHC_ADMIN ? 'isInactive!=true' : undefined,
      sort: 'name',
      page: {
        size: 10000,
      },
    },
  ]);
  const calendarViewRef = useRef<any>(null);
  const downloadMenuRef = useRef<any>(null);

  useEffect(() => {
    if (customers.data && customers.data.length > 0) {
      setCustomer(customers.data[0]);
    }
  }, [customers.data]);

  useEffect(() => {
    customers.error &&
      notification.error({
        message: ' ',
        description: customers?.error?.detail,
      });
  }, [customers.error]);

  return (
    <Flex vertical gap={16}>
      <SyncSuspense fallback={<LoadingSpin />} condition={!customers.isLoading}>
        <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
          <Space direction="vertical">
            <Typography.Text>Select Customer</Typography.Text>
            <Select
              loading={customers.isLoading}
              showSearch
              options={customers.data?.map((cust) => ({ label: cust.name, value: cust.id })) ?? []}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              value={customer?.id}
              onChange={(val) => {
                setCustomer(customers.data?.find((cust) => cust.id === val) ?? undefined);
              }}
              style={{ maxWidth: 500, minWidth: 300 }}
            />
          </Space>
        </AuthorizedComponent>
        <Space direction="vertical">
          <Button
            icon={<DownOutlined />}
            onClick={() => {
              downloadMenuRef.current.showModal();
            }}
            style={{ marginTop: 30 }}
          >
            Download Menu
          </Button>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => {
              calendarViewRef.current.showModal();
            }}
            style={{ marginBottom: 30 }}
          >
            Show scheduled meals
          </Button>
        </Space>

        <ScheduledMealsCalendarModal
          ref={calendarViewRef}
          customer={customer ?? new Customer()}
          onConfirm={() => {
            calendarViewRef.current.hideModal();
          }}
          onCancel={() => {
            calendarViewRef.current.hideModal();
          }}
        />

        <CycleMenuDownloadModal
          ref={downloadMenuRef}
          customers={customer ? [customer] : customers.data ?? []}
          onConfirm={() => {
            downloadMenuRef.current.hideModal();
          }}
          onCancel={() => {
            downloadMenuRef.current.hideModal();
          }}
        />
      </SyncSuspense>
    </Flex>
  );
};

export default MenusPage;
